/**
 * useCalculator Hook
 *
 * Canonical shared 4-function calculator hook. Provides a simple 4-function
 * calculator with keyboard support, an in-progress expression readout, and
 * a bounded calculation history. Consumed by Script Runner (Quick
 * Reference rail calculator, Calculator dialog) and Agent Workspace
 * (Calculator toolbar tool) — this hook has no knowledge of either feature.
 *
 * State:
 * - display: Current display value (a single operand's raw digits)
 * - accumulator: Running total used for arithmetic
 * - operator: Current pending operator
 * - waitingForOperand: Whether next input starts a new number
 * - expression: The full in-progress expression for the CURRENT
 *   calculation (e.g. "10 × 10"), collapsing to `display` whenever there
 *   is no pending operator context (before any operator is pressed, and
 *   immediately after `equals()`, where it deliberately equals the result
 *   only — the completed expression moves to `history` at that point, not
 *   the main display). This is the value presentation should render as the
 *   main calculator screen.
 * - history: bounded, newest-first array of completed calculations, each
 *   `{ id, expression, result }`. Only added to by a successful `equals()`
 *   call with a finite result — never for incomplete expressions, clear,
 *   operator-only states, or invalid (e.g. divide-by-zero) results. Capped
 *   at HISTORY_LIMIT entries (oldest discarded). Local-state only — no
 *   persistence, no API.
 *
 * Usage:
 * const calculator = useCalculator();
 * calculator.inputDigit('5');
 * calculator.setOperator('+');
 * calculator.equals();
 */

import { useCallback, useMemo, useRef, useState } from 'react';

// Human-readable operator glyphs, matching the shared CalculatorKeypad's
// existing button labels exactly (× ÷ − are the visually-approved glyphs
// already used there, not plain "*"/"/"/"-").
const OPERATOR_SYMBOLS = { '+': '+', '-': '−', '*': '×', '/': '÷' };

// Reasonable in-memory cap so history cannot grow without bound during a
// long call. No pre-existing repository convention for calculator history
// size was found, so this follows the value suggested for this feature.
export const HISTORY_LIMIT = 20;

export function useCalculator() {
  const [display, setDisplay] = useState("0");
  const [accumulator, setAccumulator] = useState(null);
  const [operator, setOperator] = useState(null);
  const [waitingForOperand, setWaitingForOperand] = useState(true);
  const [justEvaluated, setJustEvaluated] = useState(false);
  // The committed prefix of the in-progress expression, INCLUDING its
  // trailing pending operator (e.g. "10 ×", or "10 × 10 +" mid-chain).
  // Empty whenever there is no pending operator context. Kept separate
  // from `accumulator` because chained operators (10 × 10 + 5) collapse
  // `accumulator` to an intermediate result immediately, which would lose
  // the originally-typed text the agent should still be able to see.
  const [exprBase, setExprBase] = useState("");
  const [history, setHistory] = useState([]);
  const historyIdRef = useRef(0);

  // Parse display string to number
  const toNumber = useCallback((s) => {
    const n = parseFloat(String(s || "0").replace(/,/g, ""));
    return Number.isFinite(n) ? n : 0;
  }, []);

  // Format number for display
  const format = useCallback((n) => {
    if (!Number.isFinite(n)) return "Error";
    const asStr = String(n);
    if (asStr.length <= 14) return asStr;
    return String(Number(n.toPrecision(12)));
  }, []);

  // Clear the current calculation/expression only. History is intentionally
  // NOT cleared — it should remain useful for the rest of the call even
  // after the agent starts another calculation. (No "clear history"
  // control exists in this pass.)
  const clear = useCallback(() => {
    setDisplay("0");
    setAccumulator(null);
    setOperator(null);
    setWaitingForOperand(true);
    setJustEvaluated(false);
    setExprBase("");
  }, []);

  // Delete last character
  const backspace = useCallback(() => {
    if (waitingForOperand || justEvaluated) {
      setDisplay("0");
      setWaitingForOperand(true);
      setJustEvaluated(false);
      return;
    }

    setDisplay((prev) => {
      const s = String(prev || "0");
      const next = s.length <= 1 ? "0" : s.slice(0, -1);
      return next;
    });
  }, [justEvaluated, waitingForOperand]);

  // Input a digit (0-9)
  const inputDigit = useCallback(
    (d) => {
      setDisplay((prev) => {
        const p = String(prev || "0");

        if (waitingForOperand || justEvaluated) {
          return String(d);
        }

        if (p === "0") return String(d);
        return p + String(d);
      });
      setWaitingForOperand(false);
      setJustEvaluated(false);
    },
    [justEvaluated, waitingForOperand],
  );

  // Input decimal point
  const inputDot = useCallback(() => {
    setDisplay((prev) => {
      const p = String(prev || "0");
      if (waitingForOperand || justEvaluated) return "0.";
      if (p.includes(".")) return p;
      return p + ".";
    });
    setWaitingForOperand(false);
    setJustEvaluated(false);
  }, [justEvaluated, waitingForOperand]);

  // Compute result of operation
  const compute = useCallback((a, op, b) => {
    if (op === "+") return a + b;
    if (op === "-") return a - b;
    if (op === "*") return a * b;
    if (op === "/") return b === 0 ? NaN : a / b;
    return b;
  }, []);

  // Set operator (+, -, *, /). Also extends the in-progress expression
  // text (exprBase) in lockstep with the exact same three cases the
  // existing arithmetic already distinguishes:
  //   1. accumulator === null || operator === null -> a fresh expression
  //      is starting (the very first operator, or continuing a new
  //      calculation from a just-evaluated result) -> expression starts
  //      over as "{display} {op}".
  //   2. a new operand was typed since the last operator -> the typed
  //      operand is committed onto the expression before the new operator
  //      is appended.
  //   3. the operator key is pressed again with no new operand typed ->
  //      the pending operator is REPLACED, not appended (10 × then + ->
  //      "10 +", never "10 × +") — mirrors that this same case in the
  //      existing arithmetic below also does not recompute.
  const setOp = useCallback(
    (nextOp) => {
      const inputVal = toNumber(display);
      const opSymbol = OPERATOR_SYMBOLS[nextOp] || nextOp;
      const isFreshStart = accumulator === null || operator === null;
      const hasNewOperand = !isFreshStart && !waitingForOperand;

      if (isFreshStart) {
        setExprBase(`${display} ${opSymbol}`);
      } else if (hasNewOperand) {
        setExprBase((prev) => `${prev} ${display} ${opSymbol}`);
      } else {
        // Every operator symbol is exactly one character, so dropping the
        // last character of the previous expression text removes exactly
        // the stale pending operator, nothing else.
        setExprBase((prev) => (prev ? prev.slice(0, -1) + opSymbol : `${display} ${opSymbol}`));
      }

      // A fresh accumulator is needed both when there was none yet, AND
      // when continuing from a just-evaluated result (operator === null)
      // if the agent has already typed a NEW digit since that result (i.e.
      // !waitingForOperand) — otherwise that newly-typed number would be
      // silently discarded and the stale previous result reused instead
      // (a real bug this pass's expression display made directly visible:
      // typing "2" then "+" right after a "100" result would otherwise
      // still compute against 100, not 2). If no new digit was typed
      // (waitingForOperand still true), the existing "continue chaining
      // from the previous result" behavior is preserved unchanged.
      if (accumulator === null || (operator === null && !waitingForOperand)) {
        setAccumulator(inputVal);
      } else if (hasNewOperand) {
        const nextAcc = compute(accumulator, operator, inputVal);
        setAccumulator(nextAcc);
        setDisplay(format(nextAcc));
      }

      setOperator(nextOp);
      setWaitingForOperand(true);
      setJustEvaluated(false);
    },
    [
      accumulator,
      compute,
      display,
      format,
      operator,
      toNumber,
      waitingForOperand,
    ],
  );

  // The full in-progress expression, derived (not stored) from exprBase +
  // the live display, so it always stays correct through digit entry and
  // backspace without any of those handlers needing to know about it.
  const expression = useMemo(() => {
    if (!exprBase) return display;
    if (waitingForOperand) return exprBase;
    return `${exprBase} ${display}`;
  }, [exprBase, waitingForOperand, display]);

  // Evaluate current expression. On success: the main display collapses to
  // the RESULT ONLY (exprBase is cleared, so `expression` becomes `display`
  // on the next render) and a history record is appended capturing the
  // full expression exactly as it read immediately before evaluation.
  const equals = useCallback(() => {
    if (!operator || accumulator === null) {
      // Incomplete expression (e.g. "=" pressed with no operator ever
      // set) — nothing to evaluate, no history entry.
      setJustEvaluated(true);
      return;
    }

    const inputVal = toNumber(display);
    const result = compute(accumulator, operator, inputVal);
    const resultDisplay = format(result);
    // Unlike the live `expression` readout (which hides the second operand
    // until it has actually been typed), the completed history record must
    // reflect the operand that was ACTUALLY used in the computation — if
    // "=" is pressed right after an operator with no new digits typed, the
    // existing arithmetic above reuses the current `display` value as the
    // second operand, so the history text must include it too.
    const completedExpression = exprBase ? `${exprBase} ${display}` : display;

    setDisplay(resultDisplay);
    setAccumulator(result);
    setOperator(null);
    setWaitingForOperand(true);
    setJustEvaluated(true);
    setExprBase("");

    // Divide-by-zero (and any other non-finite result) is an invalid
    // calculation — preserve the existing "Error" display behavior, but do
    // not record it in history.
    if (Number.isFinite(result)) {
      const id = ++historyIdRef.current;
      setHistory((prev) => [{ id, expression: completedExpression, result: resultDisplay }, ...prev].slice(0, HISTORY_LIMIT));
    }
  }, [accumulator, compute, display, exprBase, format, operator, toNumber]);

  // Keyboard event handler
  const handleKeyDown = useCallback(
    (e) => {
      const k = e.key;
      if (k >= "0" && k <= "9") {
        e.preventDefault();
        inputDigit(k);
        return;
      }
      if (k === ".") {
        e.preventDefault();
        inputDot();
        return;
      }
      if (k === "Backspace") {
        e.preventDefault();
        backspace();
        return;
      }
      if (k === "Escape") {
        e.preventDefault();
        clear();
        return;
      }
      if (k === "Enter" || k === "=") {
        e.preventDefault();
        equals();
        return;
      }
      if (k === "+" || k === "-" || k === "*" || k === "/") {
        e.preventDefault();
        setOp(k);
      }
    },
    [backspace, clear, equals, inputDigit, inputDot, setOp],
  );

  return {
    display,
    expression,
    history,
    clear,
    backspace,
    inputDigit,
    inputDot,
    setOperator: setOp,
    equals,
    handleKeyDown,
  };
}

export default useCalculator;
